import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/Components/common';
import socketService from '@/services/Common/SocketService';
import ticketService, { Ticket, CreateTicketData } from '@/services/Common/TicketService';
import './WriteYourQuery.css';

const WriteYourQuery: React.FC = () => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentTickets, setRecentTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [expandedTicketId, setExpandedTicketId] = useState<number | null>(null);
  const [ticketDetails, setTicketDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general' as 'technical' | 'account' | 'billing' | 'course' | 'general',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load recent tickets on mount
  useEffect(() => {
    loadRecentTickets();

    // Connect to socket for real-time updates
    const token = localStorage.getItem('authToken');
    if (token && !socketService.isConnected()) {
      socketService.connect(token).catch(console.error);
    }
  }, []);

  const loadRecentTickets = async () => {
    try {
      setLoadingTickets(true);
      const tickets = await ticketService.getUserTickets();
      setRecentTickets(tickets.slice(0, 5)); // Show only last 5 tickets
    } catch (error: any) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Subject and description are required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const ticketData: CreateTicketData = {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority,
      };

      const newTicket = await ticketService.createTicket(ticketData);

      setSuccess(`Ticket ${newTicket.ticket_number} created successfully!`);

      // Reset form
      setFormData({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium',
      });

      // Reload recent tickets
      await loadRecentTickets();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      setError(error.message || 'Failed to create ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTicketDetails = async (ticketId: number) => {
    if (expandedTicketId === ticketId) {
      setExpandedTicketId(null);
      setTicketDetails(null);
      setReplyText('');
      return;
    }
    setExpandedTicketId(ticketId);
    setLoadingDetails(true);
    setReplyText('');
    try {
      const details = await ticketService.getTicketDetails(ticketId);
      setTicketDetails(details);
    } catch (error) {
      console.error('Error loading ticket details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !expandedTicketId) return;
    setSendingReply(true);
    try {
      await ticketService.addTicketReply(expandedTicketId, replyText.trim());
      setReplyText('');
      const details = await ticketService.getTicketDetails(expandedTicketId);
      setTicketDetails(details);
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSendingReply(false);
    }
  };

  const handleCloseTicket = async (ticketId: number) => {
    try {
      await ticketService.closeTicket(ticketId);
      await loadRecentTickets();
      if (expandedTicketId === ticketId) {
        const details = await ticketService.getTicketDetails(ticketId);
        setTicketDetails(details);
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
    }
  };

  const handleBack = () => {
    setIsNavigating(true);
    navigate(-1);
  };

  const getStatusBadgeClass = (status: string) => {
    const baseClass = 'ticket-status-badge';
    switch (status) {
      case 'open':
        return `${baseClass} status-open`;
      case 'assigned':
        return `${baseClass} status-assigned`;
      case 'in_progress':
        return `${baseClass} status-in-progress`;
      case 'resolved':
        return `${baseClass} status-resolved`;
      case 'closed':
        return `${baseClass} status-closed`;
      default:
        return baseClass;
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    const baseClass = 'ticket-priority-badge';
    switch (priority) {
      case 'urgent':
        return `${baseClass} priority-urgent`;
      case 'high':
        return `${baseClass} priority-high`;
      case 'medium':
        return `${baseClass} priority-medium`;
      case 'low':
        return `${baseClass} priority-low`;
      default:
        return baseClass;
    }
  };

  if (isNavigating) {
    return (
      <div className="query-container">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  return (
    <div className="query-container">
      <button className="query-back-button" onClick={handleBack}>
        <span className="query-back-arrow">←</span>
        <span className="query-back-text">Back</span>
      </button>

      <div className="query-content-wrapper">
        {/* Create Ticket Form */}
        <div className="query-form-card">
          <h1 className="query-title">Submit a Support Ticket</h1>
          <p className="query-subtitle">
            Our support team will review and respond to your ticket
          </p>

          {error && <div className="query-error-message">{error}</div>}
          {success && <div className="query-success-message">{success}</div>}

          <div className="query-form">
            <div className="query-form-group">
              <label className="query-label">
                Subject <span className="query-required">*</span>
              </label>
              <input
                type="text"
                name="subject"
                placeholder="Brief description of your issue"
                value={formData.subject}
                onChange={handleChange}
                className="query-input"
                required
              />
            </div>

            <div className="query-form-row">
              <div className="query-form-group">
                <label className="query-label">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="query-input"
                >
                  <option value="general">General</option>
                  <option value="technical">Technical</option>
                  <option value="account">Account</option>
                  <option value="billing">Billing</option>
                  <option value="course">Course</option>
                </select>
              </div>

              <div className="query-form-group">
                <label className="query-label">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="query-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="query-form-group">
              <label className="query-label">
                Description <span className="query-required">*</span>
              </label>
              <textarea
                name="description"
                placeholder="Please provide detailed information about your issue..."
                value={formData.description}
                onChange={handleChange}
                className="query-textarea"
                rows={8}
                required
              />
            </div>

            <div className="query-button-container">
              <button
                onClick={handleSubmit}
                className="query-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </div>
          </div>
        </div>

        {/* Recent Tickets */}
        <div className="query-recent-tickets">
          <h2 className="query-recent-title">Your Recent Tickets</h2>

          {loadingTickets ? (
            <div className="query-loading">Loading tickets...</div>
          ) : recentTickets.length === 0 ? (
            <div className="query-no-tickets">
              <p>No tickets yet. Submit your first ticket above!</p>
            </div>
          ) : (
            <div className="query-tickets-list">
              {recentTickets.map((ticket) => (
                <div key={ticket.id} className={`query-ticket-card ${expandedTicketId === ticket.id ? 'query-ticket-expanded' : ''}`}>
                  <div className="query-ticket-clickable" onClick={() => toggleTicketDetails(ticket.id)}>
                    <div className="query-ticket-header">
                      <span className="query-ticket-number">{ticket.ticket_number}</span>
                      <div className="query-ticket-badges">
                        <span className={getPriorityBadgeClass(ticket.priority)}>
                          {ticket.priority}
                        </span>
                        <span className={getStatusBadgeClass(ticket.status)}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <h3 className="query-ticket-subject">{ticket.subject}</h3>
                    <div className="query-ticket-meta">
                      <span>Category: {ticket.category}</span>
                      <span>
                        Created: {new Date(ticket.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {ticket.reply_count !== undefined && ticket.reply_count > 0 && (
                      <div className="query-ticket-replies">
                        {ticket.reply_count} {ticket.reply_count === 1 ? 'reply' : 'replies'}
                        {ticket.unread_replies !== undefined && ticket.unread_replies > 0 && (
                          <span className="query-unread-badge">{ticket.unread_replies} new</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded Detail View */}
                  {expandedTicketId === ticket.id && (
                    <div className="query-ticket-detail">
                      {loadingDetails ? (
                        <div className="query-ticket-detail-loading">Loading details...</div>
                      ) : ticketDetails ? (
                        <>
                          <div className="query-ticket-description">
                            <h4>Description</h4>
                            <div className="query-ticket-description-text">{ticketDetails.description || 'No description provided.'}</div>
                          </div>

                          <div className="query-ticket-replies-section">
                            <h4>Replies ({ticketDetails.replies?.length || 0})</h4>
                            {(!ticketDetails.replies || ticketDetails.replies.length === 0) ? (
                              <p className="query-ticket-no-replies">No replies yet. Our team will respond shortly.</p>
                            ) : (
                              <div className="query-ticket-replies-list">
                                {ticketDetails.replies.map((reply: any) => (
                                  <div key={reply.id} className={`query-ticket-reply ${reply.user_type === 'agent' ? 'query-reply-agent' : 'query-reply-user'}`}>
                                    <div className="query-reply-header">
                                      <span className="query-reply-sender">{reply.user_type === 'agent' ? 'Support Agent' : 'You'}</span>
                                      <span className="query-reply-date">{new Date(reply.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="query-reply-message">{reply.message}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                            <div className="query-ticket-reply-form">
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Type your reply..."
                                rows={3}
                                className="query-reply-textarea"
                              />
                              <div className="query-reply-actions">
                                <button
                                  onClick={handleSendReply}
                                  disabled={!replyText.trim() || sendingReply}
                                  className="query-reply-send-btn"
                                >
                                  {sendingReply ? 'Sending...' : 'Send Reply'}
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WriteYourQuery;
